import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import './PrivacyPage.css';

const PrivacyPage = () => {
  const currentYear = new Date().getFullYear();
  const [scrolled, setScrolled] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return false; // default to light for privacy page
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check for dark mode preference using the app's custom system
    const checkDarkMode = () => {
      const storedTheme = localStorage.getItem("theme");
      const isDark = storedTheme ? storedTheme === "dark" : false; // default to light for privacy page
      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to localStorage (when user toggles theme in navbar)
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        checkDarkMode();
      }
    };

    // Listen for changes to data-bs-theme attribute
    const observer = new MutationObserver(() => {
      checkDarkMode();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme']
    });

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [scrolled]);

  // Theme toggle handler
  const handleThemeToggle = () => {
    setDarkMode(prev => !prev);
  };

  // Handle theme changes
  useEffect(() => {
    document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    setIsDarkMode(darkMode);
  }, [darkMode]);

  const navbarShouldBeScrolled = scrolled || navbarExpanded;

  return (
    <div className="privacy-page">
      <PageTitle title="Privacy Policy" description="Privacy policy and data protection information for MedHive" />
      {/* Navigation Bar */}
      <nav className={`navbar custom-navbar navbar-expand-lg navbar-light fixed-top${navbarShouldBeScrolled ? ' scrolled' : ''}`}
        style={{ zIndex: 1050 }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
            <img src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"} alt="brand logo" style={{ height: "16px"}}/>

            {/*<img
              src={navbarShouldBeScrolled ? "/images/logo5.svg" : "/images/logo4.svg"}
              alt="brand logo"
              style={{ width: "32px", height: "32px", marginTop: "-4px" }}
            />
            <span className="ms-2">medhive</span>*/}
          </Link>
          <div className="d-flex align-items-center ms-auto order-lg-2">
            <button
              className="btn btn-light-lite px-3"
              onClick={handleThemeToggle}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill text-dark"></i>
              )}
            </button>
            <Link to="/login" className="btn btn-sm btn-primary ms-2">Login</Link>
            <button
              className="navbar-toggler btn-sm ms-2"
              type="button"
              aria-controls="mainNavbar"
              aria-expanded={navbarExpanded}
              aria-label="Toggle navigation"
              onClick={() => setNavbarExpanded((prev) => !prev)}
            >
              <span><i className={navbarShouldBeScrolled ? "bi bi-list" : "bi bi-list opacity-50"}></i></span>
            </button>
          </div>
          <div className={`collapse navbar-collapse justify-content-center order-lg-1${navbarExpanded ? ' show' : ''}`} id="mainNavbar">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4 gap-2 align-items-lg-center text-center">
              <li className="nav-item">
                <Link to="/" className="nav-link" onClick={() => setNavbarExpanded(false)}>Home</Link>
              </li>
              <li className="nav-item">
                <a href="#features" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Features</a>
              </li>
              <li className="nav-item">
                <a href="#pricing" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Pricing</a>
              </li>
              <li className="nav-item">
                <Link to="/about" className="nav-link" onClick={() => setNavbarExpanded(false)}>About Us</Link>
              </li>
              <li className="nav-item">
                <Link to="/contact" className="nav-link" onClick={() => setNavbarExpanded(false)}>Contact Us</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      {/* Header Section */}
      <section className="privacy-header-section">
        {/* Hero Background Elements */}
        <div className="hero-background-elements">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '220px' }}>
            <div className="col-lg-12 text-center">
              <h1 className="display-4 fw-bold mb-3 gradient-text">Privacy Policy</h1>
              <p className="lead text-muted">Last updated: {currentYear}. We are committed to protecting your privacy and ensuring the security of your data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="privacy-content">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="privacy-container bg-light-card">
                
                {/* Introduction */}
                <div className="privacy-section">
                  <h2>1. Introduction</h2>
                  <p>
                    medhive Inc. ("medhive," "we," "us," or "our") is committed to protecting your privacy. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                    when you use our medical education management platform.
                  </p>
                  <p>
                    As a healthcare technology platform, we understand the sensitive nature of medical data 
                    and are committed to maintaining the highest standards of privacy and security in compliance 
                    with applicable laws and regulations, including HIPAA.
                  </p>
                </div>

                {/* Information We Collect */}
                <div className="privacy-section">
                  <h2>2. Information We Collect</h2>
                  <h3>Personal Information</h3>
                  <p>We may collect the following types of personal information:</p>
                  <ul>
                    <li>Name, email address, and contact information</li>
                    <li>Professional credentials and affiliations</li>
                    <li>Institutional or organizational information</li>
                    <li>Account credentials and authentication data</li>
                  </ul>
                  
                  <h3>Healthcare Information</h3>
                  <p>In the course of providing our services, we may process:</p>
                  <ul>
                    <li>Rotation schedules and assignments</li>
                    <li>Evaluation data and performance metrics</li>
                    <li>Work hour logs and compliance data</li>
                    <li>Case logs and procedure documentation</li>
                    <li>Educational progress and assessment data</li>
                  </ul>
                  
                  <h3>Technical Information</h3>
                  <p>We automatically collect certain technical information:</p>
                  <ul>
                    <li>IP addresses and device information</li>
                    <li>Usage patterns and analytics data</li>
                    <li>Browser type and operating system</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>

                {/* How We Use Information */}
                <div className="privacy-section">
                  <h2>3. How We Use Your Information</h2>
                  <p>We use the information we collect to:</p>
                  <ul>
                    <li>Provide and maintain our medical education management services</li>
                    <li>Process and manage rotation schedules and evaluations</li>
                    <li>Ensure compliance with educational and regulatory requirements</li>
                    <li>Improve our platform and develop new features</li>
                    <li>Provide customer support and technical assistance</li>
                    <li>Send important updates and notifications</li>
                    <li>Comply with legal and regulatory obligations</li>
                  </ul>
                </div>

                {/* Information Sharing */}
                <div className="privacy-section">
                  <h2>4. Information Sharing and Disclosure</h2>
                  <p>We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:</p>
                  
                  <h3>Service Providers</h3>
                  <p>We may share information with trusted third-party service providers who assist us in operating our platform, such as:</p>
                  <ul>
                    <li>Cloud hosting and infrastructure providers</li>
                    <li>Data analytics and security services</li>
                    <li>Customer support and communication tools</li>
                  </ul>
                  
                  <h3>Legal Requirements</h3>
                  <p>We may disclose information when required by law or to:</p>
                  <ul>
                    <li>Comply with legal processes or government requests</li>
                    <li>Protect our rights, property, or safety</li>
                    <li>Investigate potential violations of our terms</li>
                  </ul>
                  
                  <h3>Business Transfers</h3>
                  <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</p>
                </div>

                {/* Data Security */}
                <div className="privacy-section">
                  <h2>5. Data Security</h2>
                  <p>We implement comprehensive security measures to protect your information:</p>
                  <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security audits and vulnerability assessments</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Employee training on data protection</li>
                    <li>Incident response and breach notification procedures</li>
                  </ul>
                  <p>
                    We are committed to maintaining HIPAA compliance and follow industry best practices 
                    for healthcare data protection.
                  </p>
                </div>

                {/* Data Retention */}
                <div className="privacy-section">
                  <h2>6. Data Retention</h2>
                  <p>
                    We retain your information for as long as necessary to provide our services and 
                    comply with legal obligations. The retention period varies based on the type of 
                    information and applicable regulations.
                  </p>
                  <p>
                    Healthcare data is retained in accordance with medical record retention requirements 
                    and institutional policies.
                  </p>
                </div>

                {/* Your Rights */}
                <div className="privacy-section">
                  <h2>7. Your Privacy Rights</h2>
                  <p>Depending on your location, you may have the following rights:</p>
                  <ul>
                    <li><strong>Access:</strong> Request access to your personal information</li>
                    <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                    <li><strong>Deletion:</strong> Request deletion of your information</li>
                    <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                    <li><strong>Objection:</strong> Object to certain processing activities</li>
                  </ul>
                  <p>
                    To exercise these rights, please contact us using the information provided below.
                  </p>
                </div>

                {/* Cookies and Tracking */}
                <div className="privacy-section">
                  <h2>8. Cookies and Tracking Technologies</h2>
                  <p>
                    We use cookies and similar technologies to enhance your experience on our platform. 
                    These technologies help us:
                  </p>
                  <ul>
                    <li>Remember your preferences and settings</li>
                    <li>Analyze usage patterns and improve our services</li>
                    <li>Provide personalized content and features</li>
                    <li>Ensure security and prevent fraud</li>
                  </ul>
                  <p>
                    You can control cookie settings through your browser preferences, though disabling 
                    certain cookies may affect platform functionality.
                  </p>
                </div>

                {/* Third-Party Links */}
                <div className="privacy-section">
                  <h2>9. Third-Party Links</h2>
                  <p>
                    Our platform may contain links to third-party websites or services. We are not 
                    responsible for the privacy practices of these external sites. We encourage you 
                    to review their privacy policies before providing any personal information.
                  </p>
                </div>

                {/* Children's Privacy */}
                <div className="privacy-section">
                  <h2>10. Children's Privacy</h2>
                  <p>
                    Our platform is not intended for use by individuals under the age of 18. We do 
                    not knowingly collect personal information from children. If you believe we have 
                    collected information from a child, please contact us immediately.
                  </p>
                </div>

                {/* International Transfers */}
                <div className="privacy-section">
                  <h2>11. International Data Transfers</h2>
                  <p>
                    Your information may be transferred to and processed in countries other than your 
                    own. We ensure appropriate safeguards are in place to protect your information 
                    in accordance with applicable data protection laws.
                  </p>
                </div>

                {/* Changes to Policy */}
                <div className="privacy-section">
                  <h2>12. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. We will notify you of any 
                    material changes by posting the new policy on this page and updating the "Last updated" date.
                  </p>
                  <p>
                    We encourage you to review this Privacy Policy periodically to stay informed 
                    about how we protect your information.
                  </p>
                </div>

                {/* Contact Information */}
                <div className="privacy-section">
                  <h2>13. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy or our data practices, 
                    please contact us:
                  </p>
                  <div className="contact-info-box">
                    <p><strong>Email:</strong> privacy@medhive.com</p>
                    <p><strong>Phone:</strong> 1-800-MEDHIVE</p>
                    <p><strong>Address:</strong> 123 Medical Center Dr., Suite 100, Boston, MA 02115</p>
                    <p><strong>Data Protection Officer:</strong> dpo@medhive.com</p>
                  </div>
                </div>

                {/* Effective Date */}
                <div className="privacy-section">
                  <h2>14. Effective Date</h2>
                  <p>
                    This Privacy Policy is effective as of {currentYear} and will remain in effect 
                    except with respect to any changes in its provisions in the future, which will 
                    be in effect immediately after being posted on this page.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="privacy-cta-section">
        <div className="container text-center">
          <h2 className="gradient-text mb-4">Questions About Your Privacy?</h2>
          <p className="lead mb-4">Our privacy team is here to help protect your data and answer your questions</p>
          <div className="d-flex gap-3 justify-content-center">
            <Link to="/contact" className="btn btn-light btn-lg px-5 py-3">Contact Us</Link>
            <Link to="/terms" className="btn btn-outline-light btn-lg px-5 py-3">Terms of Use</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage; 